<template>
  <transition name="slide-up">
    <div v-if="showBanner" class="cookie-banner">
      <div class="cookie-banner__container">
        <div class="cookie-banner__content">
          <div class="cookie-banner__icon">
            <i class="fa-solid fa-cookie-bite"></i>
          </div>
          <div class="cookie-banner__text">
            <h4 style="color: white;">{{ settings.title || defaultTitle }}</h4>
            <p v-html="settings.message || defaultMessage"></p>
          </div>
        </div>
        <div class="cookie-banner__actions">
          <router-link v-if="settings.policy_link" :to="settings.policy_link" class="cookie-btn cookie-btn--link">
            {{ settings.policy_link_text || defaultPolicyText }}
          </router-link>
          <button v-if="settings.show_reject_button !== false" @click="rejectCookies" class="cookie-btn cookie-btn--reject">
            {{ settings.reject_button_text || defaultRejectText }}
          </button>
          <button @click="acceptCookies" class="cookie-btn cookie-btn--accept">
            {{ settings.accept_button_text || defaultAcceptText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/api'
import { useCookieConsent } from '@/stores/cookieConsent'

export default {
  name: 'CookieBanner',
  setup() {
    const route = useRoute()
    const showBanner = ref(false)
    const settings = ref({})
    
    const currentLang = computed(() => route.params.lang || 'tr')
    
    const defaultTitle = computed(() => {
      return currentLang.value === 'tr' ? 'Çerez Politikası' : 'Cookie Policy'
    })
    
    const defaultMessage = computed(() => {
      return currentLang.value === 'tr' 
        ? 'Bu web sitesi, size en iyi deneyimi sunmak için çerezleri kullanmaktadır. Sitemizi kullanmaya devam ederek çerez politikamızı kabul etmiş olursunuz.'
        : 'This website uses cookies to provide you with the best experience. By continuing to use our site, you accept our cookie policy.'
    })
    
    const defaultAcceptText = computed(() => {
      return currentLang.value === 'tr' ? 'Kabul Et' : 'Accept'
    })
    
    const defaultRejectText = computed(() => {
      return currentLang.value === 'tr' ? 'Reddet' : 'Reject'
    })
    
    const defaultPolicyText = computed(() => {
      return currentLang.value === 'tr' ? 'Detaylı Bilgi' : 'Learn More'
    })

    const loadSettings = async () => {
      try {
        const lang = currentLang.value
        const response = await api.get(`/settings/cookie?lang=${lang}`)
        if (response.data) {
          settings.value = response.data
        }
      } catch (error) {
        console.log('Cookie settings not found, using defaults')
        settings.value = {}
      }
    }

    const checkConsent = () => {
      const consent = localStorage.getItem('cookie_consent')
      if (!consent) {
        showBanner.value = true
      }
    }

    // Cookie consent store
    const cookieConsent = useCookieConsent()

    const acceptCookies = async () => {
      cookieConsent.acceptCookies()
      showBanner.value = false
      
      // Analytics'i yükle - site ayarlarından ID'leri al
      try {
        const settingsResponse = await api.get(`/settings?lang=${currentLang.value}`)
        const siteSettings = settingsResponse.data
        
        if (siteSettings?.google_analytics_id) {
          cookieConsent.loadGoogleAnalytics(siteSettings.google_analytics_id)
        }
        if (siteSettings?.facebook_pixel_id) {
          cookieConsent.loadFacebookPixel(siteSettings.facebook_pixel_id)
        }
      } catch (error) {
        console.log('Could not load analytics settings')
      }
    }

    const rejectCookies = () => {
      cookieConsent.rejectCookies()
      showBanner.value = false
    }

    // Dil değiştiğinde ayarları yeniden yükle
    watch(currentLang, async () => {
      await loadSettings()
    })

    onMounted(async () => {
      await loadSettings()
      if (settings.value.enabled !== false) {
        checkConsent()
      }
    })

    return {
      showBanner,
      settings,
      currentLang,
      defaultTitle,
      defaultMessage,
      defaultAcceptText,
      defaultRejectText,
      defaultPolicyText,
      acceptCookies,
      rejectCookies
    }
  }
}
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #1a237e 0%, #0d47a1 100%);
  color: #fff;
  padding: 20px;
  z-index: 9999;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
}

.cookie-banner__container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 30px;
  flex-wrap: wrap;
}

.cookie-banner__content {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex: 1;
}

.cookie-banner__icon {
  flex-shrink: 0;
  opacity: 0.9;
}

.cookie-banner__text h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
}

.cookie-banner__text p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  opacity: 0.9;
}

.cookie-banner__actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

.cookie-btn {
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
  text-decoration: none;
}

.cookie-btn--accept {
  background: #fff;
  color: #1a237e;
}

.cookie-btn--accept:hover {
  background: #e8eaf6;
  transform: translateY(-2px);
}

.cookie-btn--reject {
  background: transparent;
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

.cookie-btn--reject:hover {
  border-color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.cookie-btn--link {
  background: transparent;
  color: #fff;
  text-decoration: underline;
  padding: 12px 16px;
}

.cookie-btn--link:hover {
  opacity: 0.8;
}

/* Animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.4s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (max-width: 768px) {
  .cookie-banner__container {
    flex-direction: column;
    text-align: center;
  }

  .cookie-banner__content {
    flex-direction: column;
    align-items: center;
  }

  .cookie-banner__actions {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }

  .cookie-btn {
    flex: 1;
    min-width: 120px;
  }
}
</style>
